import { useState, useEffect, useCallback } from 'react';
import { fmtMoney } from '@/lib/format';
import { useDashboard } from '@/context/DashboardContext';
import { dashboardApi } from '@/services/dashboardApi';
import { SectionHeader, PageSkeleton, LoadError } from '@/components/dashboard/MetricCard';
import { MembershipStatusCard } from '@/components/dashboard/MembershipStatusCard';
import { ReferralLinksCard } from '@/components/dashboard/ReferralLinksCard';
import { ChangePasswordCard } from '@/components/dashboard/ChangePasswordCard';
import { Check, User, Mail, Shield, Calendar, Award, Users, TrendingUp, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAccess, TIER_LABEL } from '@/components/dashboard/access';
import { motion } from 'framer-motion';

export default function DashboardPerfil() {
  const { user } = useDashboard();
  const { tier, isBroker, isMember } = useAccess();
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    dashboardApi.profile()
      .then(setProfile)
      .catch((e) => { console.error(e); setError(e.message || 'Error'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // T&C 14.6.4: el miembro decide si su email y su teléfono se muestran a su red
  const contactHidden = !!(profile || user)?.network_contact_hidden;
  const togglePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      const r = await dashboardApi.updateContactPrivacy(!contactHidden);
      setProfile((prev) => ({ ...(prev || user), network_contact_hidden: r.network_contact_hidden }));
      toast.success(r.network_contact_hidden ? 'Tu red solo verá tu nombre' : 'Tu red podrá ver tu email y tu teléfono');
    } catch (e) {
      toast.error(e.message || 'No se ha podido guardar');
    } finally {
      setSavingPrivacy(false);
    }
  };

  if (loading) return <PageSkeleton cards={0} rows={7} />;

  const p = profile || user;
  const refCode = p?.referral_code || p?.referralCode;
  // join_date llega como 2026-03-15 (o ISO con hora): se muestra dd/mm/aaaa
  const joinMatch = String(p?.join_date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const joinDate = joinMatch ? `${joinMatch[3]}/${joinMatch[2]}/${joinMatch[1]}` : (p?.join_date || '—');

  const fields = [
    { icon: User, label: 'Nombre', value: p?.name },
    { icon: Mail, label: 'Email', value: p?.email },
    { icon: Shield, label: 'Membresía', value: TIER_LABEL[tier] || 'Free' },
    isBroker && p?.level && { icon: Award, label: 'Nivel de broker', value: p?.level },
    { icon: Calendar, label: 'Miembro desde', value: joinDate },
    isBroker && { icon: Users, label: 'Afiliados directos', value: profile?.affiliates_count || 0 },
    isBroker && { icon: Users, label: 'Red (3 niveles)', value: profile?.network_count || 0 },
    isMember && { icon: TrendingUp, label: 'Comisiones pagadas', value: `${fmtMoney(profile?.total_commissions || 0, 'eur')}` },
  ].filter(Boolean);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="dashboard-perfil">
      <SectionHeader title="Mi perfil" subtitle="Tu información personal y tu enlace de invitación" />

      <MembershipStatusCard />

      {error ? <LoadError onRetry={load} /> : (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-1"
      >
        <div className="flex items-center gap-4 mb-6 pb-5 border-b border-white/[0.04]">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#c9a84c] to-[#a08339] flex items-center justify-center text-[#0a1628] text-2xl font-bold shadow-xl shadow-[#c9a84c]/10">
              {p?.name?.charAt(0)}
            </div>
            {p?.email_verified !== false && (
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-500 border-2 border-[#060d1b] flex items-center justify-center" title="Email verificado">
                <Check size={10} className="text-white" />
              </div>
            )}
          </div>
          <div>
            <h3 className="text-white text-lg font-semibold">{p?.name}</h3>
            <p className="text-[#c9a84c] text-xs uppercase tracking-wider font-medium">{TIER_LABEL[tier] || 'Free'}</p>
          </div>
        </div>
        {fields.map((f, i) => (
          <motion.div
            key={f.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25, delay: Math.min(i, 6) * 0.03 }}
            className="flex items-center gap-3 py-3 border-b border-white/[0.03] last:border-0"
          >
            <div className="w-7 h-7 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
              <f.icon size={13} className="text-gray-400" />
            </div>
            <span className="text-sm text-gray-400 w-28 sm:w-40 shrink-0">{f.label}</span>
            <span className="text-sm text-white min-w-0 break-all">{f.value}</span>
          </motion.div>
        ))}
      </motion.div>
      )}

      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6" data-testid="profile-privacy">
        <h3 className="text-white text-sm font-semibold">Tus datos dentro de tu red</h3>
        <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">
          Si te registraste con el enlace de otro miembro, los brokers Premium que están por encima de ti (hasta tres niveles)
          pueden ver tu email y tu teléfono, solo para ayudarte dentro del club (cláusula 14.6 de los T&C).
        </p>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <p className="text-sm text-gray-300 flex items-center gap-2">
            {contactHidden ? <EyeOff size={15} className="text-gray-400" /> : <Eye size={15} className="text-[#c9a84c]" />}
            {contactHidden ? 'Tu red solo ve tu nombre' : 'Tu red ve tu nombre, email y teléfono'}
          </p>
          <button type="button" onClick={togglePrivacy} disabled={savingPrivacy}
            className="inline-flex items-center justify-center gap-2 min-h-[40px] px-4 rounded-lg border border-white/10 text-sm text-gray-200 hover:border-[#c9a84c]/40 hover:text-white disabled:opacity-60"
            data-testid="profile-privacy-toggle">
            {savingPrivacy && <Loader2 size={14} className="animate-spin" />}
            {contactHidden ? 'Mostrar mi email y teléfono' : 'Ocultar mi email y teléfono'}
          </button>
        </div>
      </section>

      <ChangePasswordCard />

      <ReferralLinksCard refCode={refCode} variant="full" delay={0.2} />
    </div>
  );
}
